import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
	Avatar,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import moment from "moment";
import React, { useState } from "react";
import DeleteConfirmation from "src/Utils/DeleteConfirmation";
import { imgUrl } from "src/Helpers/utils/constants";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { IRaffleWinner } from "src/Types/module/raffleDraw/raffleDraw";

interface Props {
	winner: IRaffleWinner;
	serial: number;
}

const WinnerTableRow: React.FC<Props> = ({ winner, serial }) => {
	const [openRegenerateConfirm, setOpenRegenerateConfirm] = useState(false);

	// Regenerate Winner mutation
	const { mutate: regenerateWinner, isPending: isRegenPending } = useApiMutation<
		{ prize_id: number },
		void
	>({
		method: "POST",
		endPoint: "/api/raffle-draw/winner/regenerate",
		invalidateQueries: [
			"raffle-winner",
			"raffle-prize",
			"raffle-participant",
		],
	});

	// Delete Winner mutation
	const { mutate: deleteWinner, isPending: isDeletePending } = useApiMutation<
		void,
		void
	>({
		method: "DELETE",
		endPoint: `/api/raffle-draw/winner/${winner.id}`,
		invalidateQueries: [
			"raffle-winner",
			"raffle-prize",
			"raffle-participant",
		],
	});

	// Toggle Prize Given mutation (PATCH)
	const { mutate: togglePrizeGiven, isPending: isTogglePending } =
		useApiMutation<{ prize_given: number }, void>({
			method: "PATCH",
			endPoint: `/api/raffle-draw/winner/${winner.id}`,
			invalidateQueries: [
				"raffle-winner",
				"raffle-prize",
				"raffle-participant",
			],
		});

	const handleTogglePrizeGiven = () => {
		const nextStatus = winner.prize_given === 1 ? 0 : 1;
		togglePrizeGiven({ prize_given: nextStatus });
	};

	const avatarSrc = winner.photo
		? `${imgUrl}/${winner.photo}`
		: "/static/images/avatars/avatar2.png";

	const handleConfirmRegenerate = () => {
		setOpenRegenerateConfirm(false);
		regenerateWinner({ prize_id: winner.prize_id });
	};

	return (
		<>
			<TableRow hover>
				<TableCell>
					<Typography variant="body2" color="text.primary" fontWeight={600}>
						{serial}
					</Typography>
				</TableCell>

				<TableCell>
					<Box sx={{ display: "flex", flexDirection: "column" }}>
						<Typography variant="body2" fontWeight={700} color="primary.main">
							{winner.prize_no || "-"}
						</Typography>
						{winner.prize_details && (
							<Typography variant="caption" color="text.secondary">
								{winner.prize_details}
							</Typography>
						)}
					</Box>
				</TableCell>

				<TableCell>
					<Typography
						variant="body2"
						fontWeight={700}
						color="success.main"
						sx={{ letterSpacing: 0.5 }}
					>
						{winner.raffle_no || "-"}
					</Typography>
				</TableCell>

				<TableCell>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
						<Avatar
							src={avatarSrc}
							alt={winner.name}
							sx={{ width: 34, height: 34 }}
						/>
						<Box>
							<Typography variant="body2" fontWeight={600}>
								{winner.name || "N/A"}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								ID: {winner.user_no || "-"}
							</Typography>
						</Box>
					</Box>
				</TableCell>

				<TableCell>
					<Box sx={{ display: "flex", flexDirection: "column" }}>
						<Typography variant="body2" color="text.primary">
							{winner.phone || "-"}
						</Typography>
						{winner.email && (
							<Typography variant="body1" color="text.secondary">
								{winner.email}
							</Typography>
						)}
					</Box>
				</TableCell>

				<TableCell>
					<Chip
						size="small"
						label={(winner.user_type || "visitor").toUpperCase()}
						color={
							winner.user_type === "visitor"
								? "info"
								: winner.user_type === "b2b"
									? "secondary"
									: "warning"
						}
						sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22 }}
					/>
				</TableCell>

				<TableCell>
					<Tooltip
						title={`Click to mark as ${winner.prize_given === 1 ? "No" : "Yes"}`}
						arrow
					>
						<Chip
							size="small"
							label={
								isTogglePending
									? "Updating..."
									: winner.prize_given === 1
										? "Yes"
										: "No"
							}
							color={winner.prize_given === 1 ? "success" : "warning"}
							onClick={handleTogglePrizeGiven}
							disabled={isTogglePending}
							sx={{
								fontWeight: 600,
								fontSize: "0.75rem",
								cursor: isTogglePending ? "not-allowed" : "pointer",
								"&:hover": {
									opacity: 0.85,
								},
							}}
						/>
					</Tooltip>
				</TableCell>

				<TableCell>
					<Typography variant="caption" color="text.secondary">
						{winner.created_at ? moment(winner.created_at).format("ll, LT") : "-"}
					</Typography>
				</TableCell>

				<TableCell align="left">
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Tooltip title="Re-draw Winner" arrow>
							<span>
								<IconButton
									size="small"
									color="primary"
									disabled={
										isRegenPending || isDeletePending || isTogglePending
									}
									onClick={() => setOpenRegenerateConfirm(true)}
								>
									<AutorenewIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>

						<DeleteConfirmation
							text={`Want to delete winner for prize ${winner.prize_no}?`}
							handleDeleteFun={() => deleteWinner()}
							loading={isDeletePending || isTogglePending}
						/>
					</Box>
				</TableCell>
			</TableRow>

			{/* Re-draw Confirmation Modal */}
			<Dialog
				open={openRegenerateConfirm}
				onClose={() => setOpenRegenerateConfirm(false)}
			>
				<DialogTitle>Re-draw Winner Confirmation</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to re-draw a new winner for <b>{winner.prize_no}</b> ({winner.prize_details})? The current winner ({winner.name} - {winner.raffle_no}) will be replaced.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setOpenRegenerateConfirm(false)}
						color="inherit"
					>
						Cancel
					</Button>
					<Button
						onClick={handleConfirmRegenerate}
						variant="contained"
						color="warning"
						disabled={isRegenPending}
					>
						Re-draw Winner
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default WinnerTableRow;
